import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit'

import { sec } from '~/shell/api/rtk/security'

/**
 * Triggers interactive re-authentication when any RTK Query request is rejected
 * with a server-enforced 401.
 *
 * The primary recovery happens earlier, in the base query: a re-auth-required
 * error from the silent token call kicks off login before the request is even
 * sent. This middleware is the backstop for the cases that path cannot catch —
 * a token that is structurally valid but rejected by the backend (revoked,
 * audience mismatch, server-side session expiry). Without it, a 401 leaves the
 * app stranded: `RequireAuth` still sees a client-side authenticated session and
 * never re-redirects. `sec.requestReauth` is idempotent, so several concurrent
 * 401s collapse into a single login redirect.
 */
export const unauthorizedMiddleware: Middleware = () => (next) => (action) => {
  const isUnauthorized =
    isRejectedWithValue(action) &&
    typeof action.payload === 'object' &&
    action.payload !== null &&
    'status' in action.payload &&
    action.payload.status === 401

  if (isUnauthorized) {
    sec.requestReauth()
  }
  return next(action)
}
